import React, { useState, useContext, useCallback, useRef } from "react";
import styled from "@emotion/styled";
import _debounce from "lodash/debounce";
import Context from "../ProductsProvider";
import { useMediaQuery } from "react-responsive";

//style rules
import { Typography, Colors, Sizes } from "../../../styles";

const Search = () => {
	const { data: { params, language },
		actions: { setParams },
	} = useContext(Context);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	const [value, setValue] = useState("");
	const [expanded, setExpanded] = useState(false);

	const textInputRef = useRef(null);

	const renderIcon = () => {
		if (!isDesktopOrLaptop) {
			return <img
				src={`${mainScript.assetPath}images/svg/icons/icon-search-dark.svg`}
				alt=""
				width="20px"
				height="20px"
			/>
		} else {
			return <img
				src={`${mainScript.assetPath}images/svg/icons/icon-close-green.svg`}
				alt=""
				width="20px"
				height="20px"
			/>

		}
	};

	const handleDebounceFn = (inputValue) => {
		setParams(currentValue => {
			const searchParams = new URLSearchParams(currentValue.toString());

			if (inputValue === "") {
				searchParams.delete("q");
				return searchParams;
			}

			searchParams.set("pageNumber", 1);
			searchParams.set("q", inputValue);
			return searchParams;
		});
	}

	const debounceFn = useCallback(_debounce(handleDebounceFn, 500), []);

	const handleChange = (event) => {
		setValue(event.target.value);
		debounceFn(event.target.value);
	};

	const handleExpandButtonClick = () => {
		setExpanded(true);
		textInputRef.current.focus();
	}

	const handleCloseButtonClick = () => {
		if (!isDesktopOrLaptop) return;
		setExpanded(false);
		setValue("");
		textInputRef.current.blur();
		
		if (!params.has("q")) return;
		setParams(currentValue => {
			const searchParams = new URLSearchParams(currentValue.toString());
			searchParams.delete("q");
			return searchParams;
		});
	}

	const searchAltText = () => {
		if (language === "en") return "open search";
		return "ouvrir la recherche";
	}

	const searchText = () => {
		if (language === "en") return "Enter your search";
		return "Entrez votre recherche";
	}

	const searchPlaceholder = () => {
		if (language === "en") return "Search";
		return "Recherche";
	}

	const submitBtnText = () => {
		if (language === "en") return "Submit search";
		return "Soumettre la recherche";
	}

	return (
		<SearchContainer>
			<Form className={expanded ? "expanded" : null}
				onSubmit={(event) => event.preventDefault()}
			>
				<InputContainer>
					{
						isDesktopOrLaptop &&
					<ExpandButton
						aria-label={searchAltText()}
						tabIndex={0}
						type="button"
						onClick={handleExpandButtonClick}
					>
						<img
							src={`${mainScript.assetPath}images/svg/icons/icon-search-dark.svg`}
							alt=""
						/>
					</ExpandButton>
					}

					<InputLabel for="search">{searchText()}</InputLabel>
					<Input
						type="text"
						id="search"
						placeholder={searchPlaceholder()}
						onChange={handleChange}
						value={value}
						tabIndex={expanded ? 0 : -1}
						ref={textInputRef}
					/>
					<Icon
						aria-label={submitBtnText()}
						tabIndex={0}
						type="button"
						onClick={handleCloseButtonClick}
					>
						{renderIcon()}
					</Icon>
				</InputContainer>
			</Form>
		</SearchContainer>
	);
};

export default Search;

const SearchContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2rem;
	margin-top: 3.5rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		margin-top: unset;
		position: relative;
	}
`;

const Icon = styled.button`
	width: 1.6rem;
	height: 1.6rem;
	cursor: pointer;
	position: absolute;
	right: 2rem;
	border: none;
	background: none;
	padding: 0;

	img {
		width: 1.6rem;
		height: 1.6rem;
	}

	&::after {
		display: none;
		transition: unset;
	}

	&:hover,
	&:focus {
		transition: unset;
		padding-right: 0;
	}

	&:focus-visible {
		@media only screen and (min-width: ${Sizes.screens.large}) {
			outline: 2px inset -webkit-focus-ring-color;
		}
	}
`;

const ExpandButton = styled(Icon)`
	right: unset;
	left: 0;
	transition: left 0.5s;
	bottom: 1.5rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		&:focus-visible {
			outline: 2px inset -webkit-focus-ring-color;
		}
	}

	img {
		width: 2rem;
		height: 2rem;
}
`;

const Form = styled.form`
	position: relative;
	z-index: 11;
	align-items: baseline;
	width: 100%;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		width: 2.2rem;
		max-width: 65rem;
		position: absolute;
		left: 0;
		top: 50%;
		transform: translateY(-50%);
		overflow: hidden;
		transition: width 0.5s;
		padding: 0.2rem;

		&.expanded {
			width: 48.5rem;
			transition: width 0.5s;

			input[type="text"] {
				padding: 1rem 4rem !important;
				border: 2px solid ${Colors.base.black};
				transition: padding 0.5s, opacity 0.5s;
					
				&::placeholder {
					opacity: 1;
					transition: opacity 0.5s;
				}

				&:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not(.gfield-choice-input) {
					&:hover,
					&:focus-visible,
					&:focus {
						border: 2px solid ${Colors.fp.teal} !important;
						box-shadow: unset;
					}
				}
			}
	
			${ExpandButton} {
				left: 2rem;
				transition: left 0.5s;
				bottom: 1.7rem;
			}
		}
	}
`;

const InputContainer = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	position: relative;
`;

const Input = styled.input`
	padding: 1rem 4rem 1rem 2rem !important;
	border-radius: 3rem !important;
	margin-bottom: 0 !important;
	width: 100%;
	background-color: ${Colors.fp.pineGreen20};
	border: none;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		background-color: ${Colors.base.white};
		border: 0 solid ${Colors.base.black};
		padding: 1rem 0 !important;
		transition: padding 0.5s, opacity 0.5s;
	}

	&::placeholder {
		font-size: 1.7rem;
		line-height: 2.4rem;
		font-weight: ${Typography.fontWeight.medium};
		color: ${Colors.grey.charcoal};
		transition: opacity 0.5s;
		
		@media only screen and (min-width: ${Sizes.screens.large}) {
			opacity: 0;
		}
	}

	&:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not(.gfield-choice-input) {
		&:hover,
		&:focus-visible,
		&:focus {
			outline: none;
			border: none !important;
			box-shadow: unset;
		}
	}
`;

const InputLabel = styled.label`
	position: absolute;
	top: -22px;
	visibility: hidden;
`;
